import { buffers, eventChannel, END, EventChannel } from 'redux-saga';
import { delay, take, put, call, race, takeEvery } from 'redux-saga/effects';
import {
    Actions,
    publishThemeSuccess, publishThemeFailure,
    resetThemeStatusSuccess, resetThemeStatusFailure,
    uploadThemeProgress, clearUploadTheme, uploadThemeSuccess, uploadThemeFailure, uploadThemeRequest, publishThemeRequest,
} from 'actions/themeActions';
import { dismissPublishPending } from 'actions/editorActions';
import i18n from 'i18next';
import { makeNavigateToBuildingRoute } from './makeNavigateToBuildingRoute';
import { apiGet, apiPost, apiDelete } from './apiSagas';
import { Theme, ThemeStatus } from 'shared/models';
import { toError } from 'shared/util/toError';

const navigateToBuildingThemes = makeNavigateToBuildingRoute("themes");

export function* resetThemeStatusSaga() {
    while (true) {
        const { payload: themeId } = yield take(Actions.RESET_THEME_STATUS_REQUEST);
        try {
            const theme: Theme = yield call(apiDelete, `/api/themes/${themeId}/status`);
            yield put(resetThemeStatusSuccess(theme));
        }
        catch (e) {
            const err = toError(e);
            yield put(resetThemeStatusFailure(err, themeId));
        }
    }
}

export function* publishThemeSaga() {
    yield takeEvery(Actions.PUBLISH_THEME_REQUEST, function* (action: ReturnType<typeof publishThemeRequest>) {
        if (!action) return;
        const themeId = action.payload;
        
        if (!themeId) {
            throw new Error('themeId missing in publishThemeSaga');
        }

        let theme: Theme;
        try {
            theme = yield call(apiPost, `/api/themes/${themeId}/publish`, action.meta);
            yield put(publishThemeSuccess(theme));
        }
        catch (e) {
            const err = toError(e);
            yield put(publishThemeFailure(err));
            return;
        }

        // Pull status every second. Easier than doing server push -- and this way the server is stateless
        let lastStatus = theme.status;
        for (let i = 0; i < 120; i++) {
            yield delay(900); // Approximately once per second. Assume the get call will be ~100ms

            const cacheBust: number = yield call(Math.random as any);
            theme = yield call(apiGet, `/api/themes/${themeId}?${cacheBust.toString(36).substr(2)}`);

            // Update the store if the status changed since our last call
            if (theme.status !== lastStatus) {
                yield put(publishThemeSuccess(theme));
                lastStatus = theme.status;
            }

            // Stop requesting updates when we've reached a final state
            if (theme.status === ThemeStatus.Error ||
                theme.status === ThemeStatus.Idle) {
                break;
            }
        }

        // For now, just always dismiss. (because workflow of editor is changing)
        //// We need to manually dismiss the Publish Pending message if the editor is hidden
        //// or else it will appear the next time the user enters the Theme Editor.
        //// Also dismiss the message if the operation was successful. (let the user dismiss
        //// if there was an error)
        //const visible = yield select(getIsEditorVisible);
        //if (!visible || theme.status === ThemeStatus.Idle) {
        yield put(dismissPublishPending());
        //}
    });
}

function createUploadThemeChannel(endpoint: string, file: File) {
    return eventChannel(emitter => {
        const xhr = new XMLHttpRequest();

        const onProgress = (e: ProgressEvent) => {
            if (e.lengthComputable) {
                const progress = e.loaded / e.total;
                emitter({ progress });
            }
        };
        const onFailure = (/*e?: ProgressEvent*/) => {
            emitter({ error: 'THEME_EDITOR.ERROR.UPLOAD_FAILED' }); // TODO: make specific to uploading a theme?
            emitter(END);
        };

        // Delay starting the upload to give the opening animation (400ms) time to run
        setTimeout(() => {
            xhr.upload.addEventListener("progress", onProgress);
            xhr.upload.addEventListener("error", onFailure);
            xhr.upload.addEventListener("abort", onFailure);
            xhr.onreadystatechange = () => {
                const { readyState, status } = xhr;
                if (readyState === 4) {
                    if (status === 200) {
                        // Advance the progress bar to 100%
                        emitter({ progress: 1 });

                        // Delay the actual acknowledgement of completion so the
                        // user has time to visually process what just happened.
                        setTimeout(() => {
                            emitter({ success: true });
                            emitter(END);
                        }, 500);
                    }
                    else {
                        emitter({ error: 'THEME_EDITOR.ERROR.UPLOAD_FAILED' });
                        emitter(END);
                    }
                }
            };

            xhr.open("PUT", endpoint, true);
            xhr.setRequestHeader("Content-Type", "application/octet-stream");
            try {
                xhr.send(file);
            }
            catch (err) {
                console.error(err);
                onFailure();
            }
        }, 400);

        return () => {
            xhr.upload.removeEventListener("progress", onProgress);
            xhr.upload.removeEventListener("error", onFailure);
            xhr.upload.removeEventListener("abort", onFailure);
            xhr.onreadystatechange = null;
            xhr.abort();
        };
    }, buffers.sliding<object>(2));
}

function* uploadThemeSaga({ buildingId, displayName, width, height, file, requirements }: any) {
    let theme: Theme;
    let url: string;
    try {
        const extension = requirements["TTG"] ? 'ttg' : requirements["Matisse"] ? 'tar' : null;
        const input = { buildingId, displayName, width, height, requirements, extension };

        // Tell the server that we want to upload an existing theme file, it will give us a URL
        const result: { theme: Theme; url: string } = yield call(apiPost, `/api/themes/existing`, input);
        theme = result.theme;
        url = result.url;
    }
    catch (e) {
        const err = toError(e);
        yield put(uploadThemeFailure(err.message));
        const { timeout } = yield race({
            timeout: delay(5000),
            dismiss: take(Actions.CLEAR_UPLOAD_THEME),
        });
        if (timeout) {
            yield put(clearUploadTheme());
        }
        return;
    }

    // Upload the file to the specified URL
    const channel: EventChannel<object> = yield call(createUploadThemeChannel, url, file);
    while (true) {
        const { progress = 0, error, success } = yield take(channel);
        if (error) {
            yield put(uploadThemeFailure(i18n.t(error)));
            yield call(apiDelete, `/api/themes/${theme.id}`);
            const { timeout } = yield race({
                timeout: delay(5000),
                dismiss: take(Actions.CLEAR_UPLOAD_THEME),
            });
            if (timeout) {
                yield put(clearUploadTheme());
            }
            return;
        }
        if (success) {
            yield put(uploadThemeSuccess(theme, buildingId));
            yield delay(500);
            yield put(clearUploadTheme());
            yield call(navigateToBuildingThemes, buildingId, '/buildings');
            return;
        }

        yield put(uploadThemeProgress(progress));
    }
}

export function* uploadThemesSaga() {
    while (true) {
        const action: ReturnType<typeof uploadThemeRequest> = yield take(Actions.UPLOAD_THEME_REQUEST);
        yield uploadThemeSaga(action.payload);
    }
}
